import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import * as state from '@/components/Analysis/state';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { TabType } from '@/components/shared/BasicTabs/BasicTabs';
import { Box, Slide, Stack, Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useState } from 'react';
import { WizardProvider } from './context';
import { DataUpload } from './DataUpload';
import { Exceptions } from './Exceptions';
import { MapColumns } from './MapColumns';
import { MapValues } from './MapValues';
import { Review } from './Review';
import { WizardStep, WizardStepper } from './shared/WizardStepper';
import { Container, Layout } from './styles';
import { Snackbar } from '@/components/shared/Snackbar';

const WIZARD_STEPS_DETAILS = [
  { name: 'Upload File', iconName: 'import-alt' },
  { name: 'Map columns', iconName: 'layout-column-line' },
  { name: 'Map values', iconName: 'layout-row-line' },
  { name: 'Exceptions', iconName: 'alert' },
  { name: 'Review', iconName: 'chat-check-line' },
];

const getWizardSteps = (isLegacy: boolean): WizardStep[] => {
  const selectedSteps = isLegacy
    ? WIZARD_STEPS_DETAILS
    : [WIZARD_STEPS_DETAILS[0], WIZARD_STEPS_DETAILS[4]];
  return selectedSteps.map((step) => ({
    label: step.name,
    icon: <AbyssIcon iconName={step.iconName ?? ''} size="md+" />,
    activeIcon: <AbyssIcon iconName={(step.iconName ?? '') + '-active'} size="md+" />,
    completedIcon: <AbyssIcon iconName="checkbox-circle-fill" size="md+" />,
  }));
};

const getAllTabs = () => [
  {
    label: 'Upload File',
    component: <DataUpload />,
    id: 'upload-file',
  },
  {
    label: 'Map columns',
    component: <MapColumns />,
    id: 'map-columns',
  },
  {
    label: 'Map values',
    component: <MapValues />,
    id: 'map-values',
  },
  {
    label: 'Exceptions',
    component: <Exceptions />,
    id: 'exceptions',
  },
  {
    label: 'Review',
    component: <Review />,
    id: 'review',
  },
];

const getTabs = (isLegacy: boolean): TabType[] => {
  const allTabs = getAllTabs();
  const selectedTabs = isLegacy ? allTabs : [allTabs[0], allTabs[4]];
  return selectedTabs.map((tab, index) => ({
    ...tab,
    index,
  }));
};

export const EquipmentStrategyOnboarding = () => {
  const isLegacyEsSheet = useAtomValue(state.isLegacyEsSheet);
  const editableEsSheet = useAtomValue(state.editableEsSheet);
  const tabs = getTabs(isLegacyEsSheet);
  const wizardSteps = getWizardSteps(isLegacyEsSheet);
  const [activeStep, setActiveStep] = useState(0);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const [transitionDirection, setTransitionDirection] = useState<'left' | 'right'>('left');

  // Generate a unique key based on whether we have data - this forces re-render when state is cleared
  const componentKey = editableEsSheet ? 'with-data' : 'no-data';

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex < activeTabIndex) {
      setTransitionDirection('right');
    } else {
      setTransitionDirection('left');
    }
    setActiveStep(stepIndex);
    setActiveTabIndex(stepIndex);
  };

  const handleTabChange = (tabIndex: number) => {
    if (tabIndex < activeTabIndex) {
      setTransitionDirection('right');
    } else {
      setTransitionDirection('left');
    }
    setActiveTabIndex(tabIndex);
  };

  const { shouldShow, content } = snackbarMessage;

  return (
    <WizardProvider
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      activeTabIndex={activeTabIndex}
      setActiveTabIndex={handleTabChange}
    >
      <Layout>
        <Snackbar open={shouldShow} onClose={closeSnack}>
          {content}
        </Snackbar>
        <Stack gap={'0.8rem'}>
          <Stack direction="row" alignItems="center" gap="1.6rem">
            <Typography variant="h3" fontWeight={500} color="primary.600" letterSpacing={0}>
              ES sheet import wizard
            </Typography>
            <AbyssIcon iconName="beta" />
          </Stack>
          <Typography variant="h5" fontWeight={400} color="primary.400" letterSpacing={0}>
            Import data into your Abyss ES Tool projects
          </Typography>
        </Stack>
        <Container>
          <Box marginTop="16.4rem">
            <Stack
              gap={'4.8rem'}
              alignItems="center"
              width="100%"
              minWidth="88.4rem"
              position="relative"
            >
              <WizardStepper
                key={`wizard-stepper-${isLegacyEsSheet}`}
                steps={wizardSteps}
                activeStep={activeStep}
                useIcons
                clickable
                onStepClick={handleStepClick}
              />
              <BasicTabs
                key={componentKey}
                tabs={tabs}
                selectedTab={activeTabIndex}
                changeTab={setActiveTabIndex}
                tabNavigationStyles={{ display: 'none' }}
                TransitionComponent={Slide}
                transitionProps={{ direction: transitionDirection }}
                hideScrollGuide
              />
            </Stack>
          </Box>
        </Container>
      </Layout>
    </WizardProvider>
  );
};
